import { computeRoomNightsSummary, getNightsBetween } from "@/features/event-details-rooms/_logic/rooms";
import type { CreateEventFormData } from "../_types/wizard-types";

/**
 * Per-field completion predicates shared between each step's own Continue-button gating and
 * `computeFurthestStep`'s resume-routing logic (`CreateEventWizard.tsx`) — previously two
 * hand-maintained copies of the same checks that could drift out of sync. Only checks with
 * genuinely identical semantics at both call sites are shared here; a couple of steps (Dates,
 * Budget & decision's venue-amount threshold) apply a stricter or differently-shaped check in
 * their own Continue button than `computeFurthestStep` uses for "is this step done," so those
 * stay local to the component rather than being forced into a shared predicate that would change
 * one side's behavior.
 */

// About your event — headcount is asked on its own step (see Headcount below) so Dates can sit
// directly after Location.
export const hasEventName = (formData: CreateEventFormData): boolean => formData.name.trim().length > 0;
// A location can be `pending` while its place-details fetch resolves the real city from the
// selected address (see `LocationAutocomplete`'s optimistic entry). Excluding pending entries
// keeps Continue disabled until resolution lands, so an unresolved label (e.g. a neighborhood
// name split off a full address, before it's been resolved to its city) never gets committed
// as the event's location just because the user clicked through fast.
export const hasEventLocation = (formData: CreateEventFormData): boolean =>
  formData.formattedLocations.some((loc) => !loc.pending);
export const isAboutYourEventDone = (formData: CreateEventFormData): boolean =>
  hasEventName(formData) && hasEventLocation(formData);

// Headcount
export const hasNumPeople = (formData: CreateEventFormData): boolean => formData.numPeople > 0;
export const isHeadcountDone = hasNumPeople;

// Guestrooms
export const hasAccommodationsDates = (formData: CreateEventFormData): boolean =>
  !!formData.accommodations.checkIn && !!formData.accommodations.checkOut;
// The busiest program night's total room count across the optional per-night, per-room-type
// breakdown (`accommodationsDetailed`). Filling in the detail grid never syncs back to
// `accommodations.maxRoomsPerNight`, so this must be checked as an alternate source of truth —
// otherwise a user who only ever used the detail grid would show 0 count here even with real data.
// Only the program's own nights (check-in up to check-out) count, so a few pre/post-night staff
// rooms can't stand in for the program's room minimum; without dates, every saved night counts.
export const accommodationsPeakRoomCount = (formData: CreateEventFormData): number => {
  const { accommodations, accommodationsDetailed } = formData;
  if (!accommodationsDetailed) return accommodations.maxRoomsPerNight;
  const { checkIn, checkOut } = accommodations;
  if (checkIn && checkOut) {
    return computeRoomNightsSummary(getNightsBetween(checkIn, checkOut), accommodationsDetailed).peakRooms;
  }
  return Math.max(
    0,
    ...Object.values(accommodationsDetailed).map((counts) =>
      Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0)
    )
  );
};
export const hasAccommodationsCount = (formData: CreateEventFormData): boolean =>
  accommodationsPeakRoomCount(formData) > 0;
export const isGuestroomsDone = (formData: CreateEventFormData): boolean =>
  !formData.accommodations.needed || (hasAccommodationsDates(formData) && hasAccommodationsCount(formData));

/**
 * Whether the planner asked for at least one meeting space after saying they need meeting rooms.
 * @param formData - Wizard form state.
 * @returns True when the general session or breakout answer is "Yes".
 */
export const hasAnyMeetingSpace = (formData: CreateEventFormData): boolean =>
  formData.meetingRooms.generalSession.needed === true || formData.meetingRooms.breakout.needed === true;

// Meeting rooms — mirrors MeetingRoomsStep's own Continue-button gating. "Yes" is done once both
// sub-questions are answered and at least one is "Yes"; "No" and "No" would ask venues for zero
// rooms, so it routes a resuming user back here.
// "No" only counts as done when Guestrooms is needed — `createEventFromDraft` hard-rejects an
// event with neither, so a "No"+"No" combination must route a resuming user back here (matching
// MeetingRoomsStep's own live-flow block on that same combination) rather than waving them through
// to a submit that will fail anyway.
export const isMeetingRoomsDone = (formData: CreateEventFormData): boolean =>
  !formData.meetingRooms.needed
    ? formData.accommodations.needed
    : formData.meetingRooms.generalSession.needed !== null &&
      formData.meetingRooms.breakout.needed !== null &&
      hasAnyMeetingSpace(formData);

// Food & beverage — its own step, independent of whether meeting rooms are needed. "No" is complete
// on its own; "Yes" additionally requires at least one service pill or some detail text.
export const isFoodBeverageDone = (formData: CreateEventFormData): boolean =>
  formData.foodBeverage.needed === false ||
  (formData.foodBeverage.needed === true &&
    (Object.keys(formData.foodBeverage.meals).length > 0 || formData.foodBeverage.details.trim().length > 0));

// Dates — `computeFurthestStep` only needs "is any eventDates value present," a looser check than
// `DatesStep`'s own `hasPrimaryDates` (which validates the specific range shape to read start/end
// into the picker), so this atomic stays computeFurthestStep-only.
export const isDatesDone = (formData: CreateEventFormData): boolean =>
  !!formData.eventDates && formData.flexibility !== null;

// Agenda — entirely optional; any engagement signal counts as "done" for resume purposes. No step
// component consumes this (Agenda has no data-driven Continue gate), but centralizing it here keeps
// `computeFurthestStep` a simple lookup rather than inlined logic.
export const isAgendaDone = (formData: CreateEventFormData): boolean => formData.agendaItems.length > 0;

// Budget & decision
export const hasDecisionDeadline = (formData: CreateEventFormData): boolean => !!formData.decisionDeadline;
export const hasDecisionFactors = (formData: CreateEventFormData): boolean => formData.decisionFactors.length > 0;
export const isBudgetDecisionDone = (formData: CreateEventFormData): boolean =>
  (formData.budgetUnknown || formData.budgetVenue !== null) &&
  hasDecisionDeadline(formData) &&
  hasDecisionFactors(formData);
